/**
 * Answers to "Ask the parents" (migration 0045) that name the same child. Desk, the owner's To do
 * list and the phone all use this rule, so they agree on what is a clash.
 *
 * - Two parents: exactly two people answer for a child, one says "mother" and the other "father".
 *   Link both puts them in one family: the first answer makes the family, the second person becomes
 *   its second adult.
 * - Clash: any other mix of people on one child (two mothers, a coach and a parent, no relation),
 *   and an answer that pairs with a different person on each of its children.
 *   The owner picks one, or links both to one family by hand.
 */

type RelationValue = "mother" | "father" | "other" | null;

export type ClaimForPairing = {
  id: string;
  contactId: string;
  relation: RelationValue;
  /** The names the answer is about. */
  pendingIds: string[];
};

export type ClaimPairing = {
  /** Answers that clash with another answer. */
  clash: Set<string>;
  /** Two-parent answers: the ids of the other parent's answers that name the same child. */
  partners: Map<string, string[]>;
};

/** One relation for each person on a child, or null when the person gave none, or more than one. */
function relationOf(claims: ClaimForPairing[]): RelationValue {
  const relations = new Set(claims.map((claim) => claim.relation));
  return relations.size === 1 ? [...relations][0]! : null;
}

export function pairClaims(claims: ClaimForPairing[]): ClaimPairing {
  const byName = new Map<string, ClaimForPairing[]>();
  for (const claim of claims) {
    for (const id of claim.pendingIds) byName.set(id, [...(byName.get(id) ?? []), claim]);
  }

  const clash = new Set<string>();
  const partners = new Map<string, Set<string>>();
  for (const onName of byName.values()) {
    const people = new Map<string, ClaimForPairing[]>();
    for (const claim of onName) people.set(claim.contactId, [...(people.get(claim.contactId) ?? []), claim]);
    if (people.size < 2) continue;
    const [a, b] = [...people.values()];
    const relations = people.size === 2 ? new Set([relationOf(a!), relationOf(b!)]) : new Set<RelationValue>();
    if (relations.has("mother") && relations.has("father")) {
      for (const claim of a!) for (const other of b!) partners.set(claim.id, (partners.get(claim.id) ?? new Set()).add(other.id));
      for (const claim of b!) for (const other of a!) partners.set(claim.id, (partners.get(claim.id) ?? new Set()).add(other.id));
    } else {
      for (const claim of onName) clash.add(claim.id);
    }
  }

  // One answer with a different other parent on different children (a mother, and a father for each
  // child) is no single pair: Link both would put the three people in one family (review F1.1).
  for (const [id, others] of partners) {
    const contacts = new Set([...others].map((other) => claims.find((claim) => claim.id === other)!.contactId));
    if (contacts.size > 1) clash.add(id);
  }
  // Link both links the partners too, so a partner of a clash is a clash.
  for (let changed = true; changed; ) {
    changed = false;
    for (const [id, others] of partners) {
      if (clash.has(id) || ![...others].some((other) => clash.has(other))) continue;
      clash.add(id);
      changed = true;
    }
  }
  for (const id of clash) partners.delete(id);
  return { clash, partners: new Map([...partners].map(([id, others]) => [id, [...others]])) };
}
